import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type {
  Appointment,
  Blackout,
  Certificate,
  ClientDetail,
  CreditEntry,
  Hours,
  Product,
  Resource,
  Service,
  Session,
} from '../wire';
import type { OutboxEntry } from '../outbox/entry';

/**
 * The device's two halves, in one IndexedDB database: **the mirror and the
 * outbox** (spec §8).
 *
 * They are in one database on purpose. An intent settles and a mirrored row
 * changes as a consequence, and the two writes want to be one transaction —
 * IndexedDB will only give that across stores of the same database. Splitting
 * them would let the device restart between "the booking landed" and "the intent
 * is done", which is the one interleaving that could show an appointment twice.
 *
 * `appointments` is indexed `by-day` — the ISO date in the **tenant's** timezone,
 * computed on write. Today's book is a range query on an index rather than a
 * full scan with a timezone conversion per row, and the day boundary is the
 * tenant's rather than the iPad's, which matters the moment a device travels.
 */

export interface MirroredAppointment extends Appointment {
  /** `YYYY-MM-DD` in the tenant timezone. Derived on write; never from a server. */
  day: string;
}

export interface MetaRecord {
  key: string;
  value: unknown;
}

/**
 * What a guest has on account, mirrored per client.
 *
 * Balances arrive for the whole tenant in one read (`GET /v1/credit/outstanding`
 * — bounded, and only clients with a balance are in it); the `entries` history
 * arrives per client when somebody opens their record. So a row may hold
 * balances and no history, which is exactly what the checkout needs and half of
 * what the client screen shows.
 *
 * The two pools are stored separately and never summed, because they are
 * different liabilities: §7.4 rule 2 makes `cash` money the guest handed over
 * and `gift` a promise the owner made.
 */
export interface MirroredCredit {
  client_id: string;
  cash_cents: number;
  gift_cents: number;
  /** Absent until somebody opens this client's record with a network. */
  entries?: CreditEntry[];
}

export interface YourstoreDB extends DBSchema {
  appointments: {
    key: string;
    value: MirroredAppointment;
    indexes: { 'by-day': string };
  };
  clients: {
    key: string;
    value: ClientDetail;
    indexes: { 'by-name': string };
  };
  services: { key: string; value: Service };
  resources: { key: string; value: Resource };
  blackouts: { key: string; value: Blackout };
  /** Retail, mirrored because §8.1 names products in the working set. */
  products: { key: string; value: Product };
  /** Open certificates, likewise — and the code lookup at checkout is local. */
  certificates: { key: string; value: Certificate };
  credit: { key: string; value: MirroredCredit };
  meta: { key: string; value: MetaRecord };
  outbox: {
    key: string;
    value: OutboxEntry;
    indexes: { 'by-seq': number };
  };
}

export type AppDb = IDBPDatabase<YourstoreDB>;

export const DB_NAME = 'yourstore-app';
/** 2: MVP 4 added `products`, `certificates` and `credit` to the working set. */
export const DB_VERSION = 2;

/** Meta keys. Strings in one place, so a typo is a compile error. */
export const META = {
  /** The SSE resume cursor (`Last-Event-ID`). */
  cursor: 'cursor',
  /** When the mirror last heard from the server, as an ISO instant. */
  lastSyncAt: 'lastSyncAt',
  /** `GET /v1/session`, cached so the shell can paint offline. */
  session: 'session',
  /** `GET /v1/settings/hours`. */
  hours: 'hours',
  /** The `[from, to]` day range `appointments` is known to cover. */
  window: 'window',
  /** The next outbox sequence number — the total order intents replay in. */
  seq: 'seq',
  /** The last-fetched report snapshot, and when it was fetched. */
  reports: 'reports',
  /** Today's takings by tender, and the day they are for (`today-takings.ts`). */
  todayTakings: 'todayTakings',
  /**
   * The tenant id that has been seen to hold a reservation — **latched**: once
   * a shop has had one, the Reservations tab never goes away again, offline or
   * not (`Mirror.refreshTenantFacts`).
   */
  hadReservation: 'hadReservation',
  /**
   * The latest `GET /v1/newsletter/sender` answer's `configured`, with the
   * tenant it is about — **not** latched: a sender can be unconfigured again.
   */
  newsletterSender: 'newsletterSender',
} as const;

export interface MirrorWindow {
  from: string;
  to: string;
}

/**
 * Open it, creating whatever this version has that the device's copy does not.
 *
 * The upgrade is written as "create what is missing" rather than as a chain of
 * `if (oldVersion < n)` steps, because every store here is a **cache of the
 * cloud** and none of them is a migration in the usual sense: a device that
 * jumps from version 1 to version 3 wants the same three empty stores as one
 * that arrives from version 2, and the next hydrate fills them either way. The
 * outbox is the one store that holds work the cloud has not seen, and it is
 * never dropped or rebuilt — which is why nothing here touches it after
 * creation.
 */
export async function openAppDb(name: string = DB_NAME): Promise<AppDb> {
  return openDB<YourstoreDB>(name, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('appointments')) {
        const appointments = db.createObjectStore('appointments', { keyPath: 'appointment_id' });
        appointments.createIndex('by-day', 'day');
      }
      if (!db.objectStoreNames.contains('clients')) {
        const clients = db.createObjectStore('clients', { keyPath: 'client_id' });
        clients.createIndex('by-name', 'display_name');
      }
      if (!db.objectStoreNames.contains('services')) {
        db.createObjectStore('services', { keyPath: 'service_id' });
      }
      if (!db.objectStoreNames.contains('resources')) {
        db.createObjectStore('resources', { keyPath: 'resource_id' });
      }
      if (!db.objectStoreNames.contains('blackouts')) {
        db.createObjectStore('blackouts', { keyPath: 'blackout_id' });
      }
      if (!db.objectStoreNames.contains('products')) {
        db.createObjectStore('products', { keyPath: 'product_id' });
      }
      if (!db.objectStoreNames.contains('certificates')) {
        db.createObjectStore('certificates', { keyPath: 'certificate_id' });
      }
      if (!db.objectStoreNames.contains('credit')) {
        db.createObjectStore('credit', { keyPath: 'client_id' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('outbox')) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('by-seq', 'seq');
      }
    },
  });
}

export async function readMeta<T>(db: AppDb, key: string): Promise<T | undefined> {
  const row = await db.get('meta', key);
  return row === undefined ? undefined : (row.value as T);
}

export async function writeMeta(db: AppDb, key: string, value: unknown): Promise<void> {
  await db.put('meta', { key, value });
}

/**
 * Everything this device knows, gone.
 *
 * Used on sign-out, and it takes the outbox with it. That is the honest
 * behaviour: an unsent intent belongs to the person who typed it and to the
 * tenant they were signed into, and carrying it across a sign-out is how a
 * booking ends up replayed into somebody else's book — the exact write
 * `core-api`'s middleware refuses with a `403` and which should never have been
 * sent in the first place.
 */
export async function clearDevice(db: AppDb): Promise<void> {
  const stores = [
    'appointments',
    'clients',
    'services',
    'resources',
    'blackouts',
    'products',
    'certificates',
    'credit',
    'meta',
    'outbox',
  ] as const;
  const tx = db.transaction(stores, 'readwrite');
  await Promise.all(stores.map((store) => tx.objectStore(store).clear()));
  await tx.done;
}

export type { Session };
export type { Hours };
